import { KeyValuePipe, type KeyValue } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    computed,
    inject,
    input,
    type OnDestroy,
    type OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PopoverModule } from 'ngx-bootstrap/popover';

import { amountConfig, paymentCurrencies, paymentsList, paymentTypeNames } from '@common/constants/payment-form.const';
import { REG_EXP } from '@common/constants/regexp.const';

import { ELanguages } from '@common/enums';
import { EPaymentType } from '@common/enums/payment.enum';

import { getPaymentMethodCurrency } from '@common/helpers/payment';

import type {
    PaymentElementFormGroup,
    PaymentElementFormType,
    RroElementFormType,
} from '@common/types/payment-element.form';

import type { PaymentElement, PaymentMethod } from '@integrations/payment-methods';
import {
    findPaymentMethodForType,
    isMatchingPaymentType,
    mapAvailablePaymentMethods,
} from '@integrations/payment-methods';

import { SpInputNumComponent } from '../../sp-form-controls/sp-input-num/sp-input-num.component';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../../ui/sp-switch/src/sp-switch.component';

@Component({
    selector: 'sp-payment-method-settings',
    templateUrl: './sp-payment-method.component.html',
    styleUrls: ['./sp-payment-method.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        KeyValuePipe,
        ReactiveFormsModule,
        TranslateModule,
        BsDropdownModule,
        PopoverModule,
        SpInputNumComponent,
        SpSwitchComponent,
    ],
})
export class SpPaymentMethodComponent implements OnInit, OnDestroy {
    public readonly paymentMethods = input.required<PaymentMethod[]>();
    public readonly paymentElements = input.required<PaymentElement[]>();
    public readonly itemForm = input.required<PaymentElementFormGroup>();
    public readonly shopCurrency = input<string>();
    public readonly showRroSettings = input<boolean>(true);
    public readonly isDisabledPaymentMethodSelectForCart = input<boolean>(false);

    public readonly availablePaymentMethods = computed(() => mapAvailablePaymentMethods(this.paymentMethods()));

    private readonly translate = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);

    public paymentMethodByType: PaymentMethod = null;

    public readonly amountConfig = amountConfig;
    public readonly paymentsList = structuredClone(paymentsList);
    public readonly paymentTypeNames = paymentTypeNames;
    public readonly paymentCurrenciesList = paymentCurrencies;

    protected readonly EPaymentType = EPaymentType;

    protected cachedInitValue = null;

    public ngOnInit(): void {
        this.cachedInitValue = this.itemForm().value;

        this.itemForm()
            .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.itemForm().markAsDirty({ onlySelf: true });
            });

        this.itemForm()
            .get('isEditableTotalCost')
            .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isEditable) => {
                if (isEditable) this.amount.patchValue(1);
            });

        this.paymentMethodByType =
            this.cachedInitValue.type === EPaymentType.custom
                ? this.cachedInitValue
                : this.paymentMethods().find(
                      (payment: PaymentMethod) => payment.id === this.cachedInitValue.payment_method_id
                  );

        this.getFilteredPaymentList();

        this.setDefaultPaymentMethodName(this.paymentMethodByType?.type);
    }

    private getFilteredPaymentList() {
        const usedPaymentMethods = this.paymentElements()
            .map((paymentElement) => paymentElement.type)
            .filter((type) => !isMatchingPaymentType(type, this.paymentMethodByType?.type));

        for (const key in this.paymentsList) {
            const isUsed = usedPaymentMethods.some((usedType) =>
                isMatchingPaymentType(this.paymentsList[key].type, usedType)
            );
            if (isUsed && key !== EPaymentType.custom.toString()) delete this.paymentsList[key];
        }
    }

    public paymentIndexOrder = (akv: KeyValue<string, any>, bkv: KeyValue<string, any>): number => {
        const a = akv.value.index;
        const b = bkv.value.index;

        return a > b ? 1 : b > a ? -1 : 0;
    };

    public setAmount(value: number): void {
        this.amount.patchValue(value);
    }

    public setCurrency(value: string): void {
        this.currency.patchValue(value);
    }

    public setDefaultPaymentMethodName(type: EPaymentType): void {
        if (!this.newPaymentName.value || this.newPaymentName.value === '') {
            if (type !== EPaymentType.custom) {
                this.newPaymentName.patchValue(this.translate.instant(this.paymentsList[type]?.label));
            } else {
                this.newPaymentName.patchValue(this.translate.instant('custom_payment_label'));
            }
        }
    }

    public setType(type: EPaymentType): void {
        const isCustom = type === EPaymentType.custom;
        this.paymentMethodByType = isCustom ? null : findPaymentMethodForType(this.paymentMethods(), type);
        const resolvedType = (this.paymentMethodByType?.type as EPaymentType) ?? type;

        const userCurrency = this.shopCurrency();
        const currency =
            isCustom && userCurrency
                ? userCurrency
                : getPaymentMethodCurrency(this.paymentMethodByType?.settings) || 'USD';

        this.itemForm().patchValue({
            type: resolvedType,
            currency,
            payment_method_id: isCustom ? 'custom_payment_id' : (this.paymentMethodByType?.id ?? null),
            isEditableTotalCost: false,
            amount: 1,
            customPaymentName: this.translate.instant('custom_payment_label'),
            newPaymentName: isCustom
                ? this.translate.instant('custom_payment_label')
                : this.translate.instant(this.paymentsList[resolvedType]?.label),
        });
        this.setRroIsActive(false);
        this.id.patchValue(null);
    }

    public setPaymentMethodId(paymentMethodByType: PaymentMethod): void {
        this.paymentMethodByType = paymentMethodByType;
        this.itemForm().patchValue({
            currency: getPaymentMethodCurrency(this.paymentMethodByType?.settings),
            payment_method_id: this.paymentMethodByType.id,
        });
        if (
            Number(this.type.value) === EPaymentType.stripeConnect &&
            paymentMethodByType.type === EPaymentType.stripe
        ) {
            this.type.setValue(EPaymentType.stripe);
        }

        if (
            Number(this.type.value) === EPaymentType.stripe &&
            paymentMethodByType.type === EPaymentType.stripeConnect
        ) {
            this.type.setValue(EPaymentType.stripeConnect);
        }
    }

    public setRroIsActive(value: boolean): void {
        this.rroIsActive.setValue(value);
        this.updateIdValidation();
        if (value) {
            this.rro_info.patchValue({
                amount: 1,
                cost: this.amount.value,
                price: this.amount.value,
            });
        }
    }

    public getPaymentIconSrc(type: PaymentMethod['type']): string {
        return `./assets/img/websites/payment-icons/sm/${paymentTypeNames[type]}-sm.svg`;
    }

    public get saveDisablePredicate(): boolean {
        return (
            !this.availablePaymentMethods()?.[this.type.value] ||
            this.amount.invalid ||
            this.id.invalid ||
            this.customPaymentName.invalid ||
            this.newPaymentName.invalid
        );
    }

    public get paymentMethodsByType(): PaymentMethod[] {
        return this.paymentMethods().filter((payment: PaymentMethod) =>
            isMatchingPaymentType(payment.type, this.type.value)
        );
    }

    public get type(): PaymentElementFormType['type'] {
        return this.itemForm().get('type') as PaymentElementFormType['type'];
    }

    public get amount(): PaymentElementFormType['amount'] {
        return this.itemForm().get('amount') as PaymentElementFormType['amount'];
    }

    public get currency(): PaymentElementFormType['currency'] {
        return this.itemForm().get('currency') as PaymentElementFormType['currency'];
    }

    public get isEditableTotalCost(): PaymentElementFormType['isEditableTotalCost'] {
        return this.itemForm().get('isEditableTotalCost') as PaymentElementFormType['isEditableTotalCost'];
    }

    public get paymentMethodId(): PaymentElementFormType['payment_method_id'] {
        return this.itemForm().get('payment_method_id') as PaymentElementFormType['payment_method_id'];
    }

    public get rro_info(): PaymentElementFormType['rro_info'] {
        return this.itemForm().get('rro_info') as PaymentElementFormType['rro_info'];
    }

    public get rroIsActive(): PaymentElementFormType['rro_isActive'] {
        return this.itemForm().get('rro_isActive') as PaymentElementFormType['rro_isActive'];
    }

    public get id(): RroElementFormType['id'] {
        return this.rro_info.get('id') as RroElementFormType['id'];
    }

    private updateIdValidation() {
        const validators =
            this.type.value === EPaymentType.liqpay
                ? [Validators.required, Validators.pattern(REG_EXP.NUMBER)]
                : [Validators.required];
        this.id.setValidators(this.rroIsActive.value ? validators : null);
        this.id.updateValueAndValidity({ emitEvent: false });
    }

    public get rroLiqHelpLink(): string {
        const lang = this.translate.currentLang as ELanguages;

        switch (lang) {
            case ELanguages.RU:
                return 'https://sendpulse.com/ru/knowledge-base/landing-page/website-builder/rro-liqpay';
            case ELanguages.UA:
                return 'https://sendpulse.com/ua/knowledge-base/landing-page/website-builder/rro-liqpay';
            default:
                return 'https://sendpulse.com/knowledge-base/landing-page/website-builder/rro-liqpay';
        }
    }

    public get rroMonoHelpLink(): string {
        const lang = this.translate.currentLang as ELanguages;

        switch (lang) {
            case ELanguages.RU:
                return 'https://sendpulse.com/ru/knowledge-base/landing-page/website-builder/rro-monobank';
            case ELanguages.UA:
                return 'https://sendpulse.com/ua/knowledge-base/landing-page/website-builder/rro-monobank';
            default:
                return 'https://sendpulse.com/knowledge-base/landing-page/website-builder/rro-monobank';
        }
    }

    public get isCustomPaymentType(): boolean {
        return this.type.value === EPaymentType.custom;
    }

    public get isCryptomusPaymentType(): boolean {
        return this.type.value === EPaymentType.cryptomus;
    }

    public get customPaymentName(): PaymentElementFormType['customPaymentName'] {
        return this.itemForm().get('customPaymentName') as PaymentElementFormType['customPaymentName'];
    }

    public get newPaymentName(): PaymentElementFormType['newPaymentName'] {
        return this.itemForm().get('newPaymentName') as PaymentElementFormType['newPaymentName'];
    }

    public ngOnDestroy() {
        if (this.itemForm().dirty) {
            this.itemForm().patchValue(this.cachedInitValue);
        }

        this.updateIdValidation();
    }
}
